import { useEffect } from 'react';
import { useDatabridge, useDatabridgeSubscribe } from '@xp-utilities/web';
import { useCustomTealiumTrigger } from '../../hooks/useTealiumTrigger';

/**
 * useProfileCompletionAnalytics
 * ----------------------------
 * Custom hook to extract profile completion analytics data from userInfo and Databridge.
 * Returns { completed, total, completedFields, isAddressComplete }
 *
 * @param {Object} userInfo - User information object (may be undefined or partial)
 * @returns {Object} { completed, total, completedFields, isAddressComplete }
 *
 * How it works:
 * - Subscribes to address completion status via Databridge.
 * - Extracts relevant user info fields from the userInfo object.
 * - Parses the profileCompletion value (e.g., "6/6 Completed") to get completed and total fields.
 * - Builds a string of completed fields based on which fields are truthy.
 */
function useProfileCompletionAnalytics(userInfo) {
  // Subscribe to address completion status from Databridge.
  const isAddressComplete = useDatabridgeSubscribe('analytics.global.account.profile_completion.has_default_address');

  // Destructure relevant fields from userInfo, defaulting to empty object if undefined.
  const {
    profileCompletion,
    preferences,
    email,
    phone,
    firstName,
    lastName,
  } = userInfo || {};

  // Parse completed/total fields from profileCompletion value (e.g., "6/6 Completed").
  // - Split on '/' to separate completed and total.
  // - Trim and extract the number before any extra text (e.g., "6 Completed" -> 6).
  const value = profileCompletion?.value || '';
  // Example value: "6/6 Completed"
  const [completed, total] = value.split('/').map((part) => Number(part.trim().split(' ')[0]));

  // Build a string listing all completed fields, only including those that are truthy.
  // Each field is only included if it is present (truthy).
  const completedFields = [
    email && 'email & password',
    phone && 'phone number',
    (firstName && lastName) && 'first & last name',
    isAddressComplete && 'my address',
    preferences?.birthDay && 'my birthday',
    preferences?.gender && 'shopping preferences',
  ]
    .filter(Boolean) // Remove any falsey entries
    .join(' & '); // Join with ' & ' as separator

  return {
    completed, total, completedFields, isAddressComplete,
  };
}

/**
 * useSetDatabridgeWithProfileCompletionAnalytics
 * ---------------------------------------------
 * Custom hook to update analytics-related databridge values for profile completion.
 * Only triggers updates when userInfo or relevant fields change.
 *
 * @param {Object} userInfo - User information object containing profile and preferences.
 *
 * How it works:
 * - Uses useProfileCompletionAnalytics to get analytics values.
 * - Updates analytics-related Databridge keys with the computed values.
 */
export const useSetDatabridgeWithProfileCompletionAnalytics = (userInfo) => {
  // Databridge setters for analytics fields.
  // These allow us to update the analytics state in the global data layer.
  const [, setNumberOfCompletedFields] = useDatabridge('user.profile.numberOfCompletedFields');
  const [, setTotalFields] = useDatabridge('user.profile.totalFields');
  const [, setCompletedFields] = useDatabridge('user.profile.completedFields');

  // Use the shared analytics hook to get computed analytics values.
  const { completed, total, completedFields } = useProfileCompletionAnalytics(userInfo);

  useEffect(() => {
    // Guard: Only run effect if userInfo is present and not empty.
    if (!userInfo) return;

    // Update analytics-related Databridge keys with the computed values.
    setNumberOfCompletedFields(completed);
    setTotalFields(total);
    setCompletedFields(completedFields);
  }, [
    userInfo,
    completed,
    total,
    completedFields,
    setNumberOfCompletedFields,
    setTotalFields,
    setCompletedFields,
  ]);
};

/**
 * useUpdateAccountSettingsTealiumEvent
 * ------------------------------------
 * Hook to trigger a Tealium analytics event for account settings update.
 * Uses the same analytics hook for consistency.
 *
 * @param {Object} userInfo - User information object
 * @returns {Function} - Tealium trigger function
 *
 * How it works:
 * - Uses useProfileCompletionAnalytics to get analytics values.
 * - Prepares analyticsData object for Tealium.
 * - Returns the custom Tealium trigger hook.
 */
export const useUpdateAccountSettingsTealiumEvent = (userInfo) => {
  // Get analytics values using the shared analytics hook.
  const { completed, total, completedFields } = useProfileCompletionAnalytics(userInfo);

  // Prepare analytics data for Tealium.
  const analyticsData = {
    eventName: 'universal_click',
    eventData: {
      event_name: 'click',
      data_text: 'profile update',
      data_action: 'update',
      number_profile_complete_fields: completed || 0,
      total_profile_complete_fields: total || 0,
      profile_complete_fields: completedFields,
    },
  };

  // returns the custom Tealium trigger hook.
  return useCustomTealiumTrigger(analyticsData);
};
